/**
 * ThemeProvider · Les Concierges® multi-tenant theming
 *
 * Adopted from TDC-Gift-LC-Design-Tokens-and-Multi-Tenant-Theming.md (23 May 2026).
 *
 * What this does:
 *   - Wraps any tenant-aware page
 *   - When given a tenant theme, overrides ONLY accent + highlight + fonts
 *   - Base palette (cream/navy ink) is code-enforced and cannot be tenant-overridden
 *   - The Concierge® trademark zones are rendered separately and cannot be hidden
 *
 * Default tenant is "lesconcierge" — the LC house theme.
 * Sample tenants we may add: "accenture", "mphasis", "hdfc"
 */

import React, { useEffect } from "react";
import { BACKEND_URL } from "@/lib/backend-url";

// PROTECTED TRADEMARK ZONES — these render regardless of tenant config
export const PROTECTED_TRADEMARK_ZONES = {
  footer_brand_line: "The AI listens. The humans deliver. · Les Concierges® since 1998",
  human_concierge_credit: "Closed by a Les Concierges® Concierge®",
  mira_attribution: "Mira · the Les Concierges® AI · since 1998",
};

// House theme — what we ship with when no tenant context is provided.
// Phone/WhatsApp/email are RESOLVED LIVE from /api/lc/tenant/contact
// at boot; the null sentinels below are intentional. Never hardcode
// concierge contact details here.
export const DEFAULT_THEME = {
  tenant_id: "lesconcierge",
  tenant_name: "Les Concierges®",
  logo_url: null,
  favicon_url: null,
  hero_image_url: null,
  accent_color: null,           // null = use --lc-navy
  accent_hover_color: null,
  highlight_color: null,        // null = use --lc-brass
  font_display: null,           // null = Fraunces
  font_body: null,              // null = Inter
  welcome_message: "Welcome. How may we help?",
  mira_intro:
    "I'm Mira, your Concierge®. Whatever you're hoping for — a table, a journey, a gesture, a problem quietly solved — tell me in your own words, in any language you think in. I listen first. Then our team makes it real.",
  placeholder_text: "What can we arrange for you?",
  // Live-resolved fields (populated on boot via /api/lc/tenant/contact).
  // Components that render these should tolerate null briefly.
  whatsapp_number: null,
  whatsapp_display: null,
  phone_number: null,
  phone_display: null,
  phone_hours: "Mon–Sat · 9 AM – 9 PM IST",
  inbound_email: null,
  show_email_channel: true,
  show_whatsapp_channel: true,
  show_phone_channel: true,
  enabled_pillars: [
    "everyday_life",
    "family_care",
    "travel",
    "documentation",
    "moments_of_life",
    "wellness",
  ],
  email_gate_level: "soft",
  allowed_email_domains: [],
  trademark_position: "footer",
};

export const ThemeContext = React.createContext({ theme: DEFAULT_THEME });

export function ThemeProvider({ tenant, children }) {
  // Start with the tenant's seed theme (if any) merged onto defaults.
  const seedTheme = tenant?.theme || DEFAULT_THEME;
  const [theme, setTheme] = React.useState(seedTheme);

  // v3.23 — Resolve concierge phone/WhatsApp/email LIVE from the
  // tenant's contact record. The backend exposes this as a public
  // GET so the chat surface, footer, share links etc. all read from
  // ONE source of truth (per tenant).
  React.useEffect(() => {
    let cancelled = false;
    const BACKEND = BACKEND_URL || "";
    async function loadTenantContact() {
      try {
        const r = await fetch(`${BACKEND}/api/lc/tenant/contact`, {
          credentials: "omit",
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setTheme((prev) => ({
          ...prev,
          phone_number: data.phone || prev.phone_number,
          phone_display: data.phone_display || prev.phone_display,
          whatsapp_number: data.whatsapp_number || prev.whatsapp_number,
          whatsapp_display: data.whatsapp_display || prev.whatsapp_display,
          inbound_email: data.email || prev.inbound_email,
          tenant_id: data.tenant_id || prev.tenant_id,
          tenant_name: data.tenant_name || prev.tenant_name,
        }));
      } catch {
        // Silent — leave nulls in place; downstream components
        // already tolerate missing contact fields.
      }
    }
    loadTenantContact();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // Only override the accent + highlight, never the base palette
    if (theme.accent_color) {
      root.style.setProperty("--lc-accent", theme.accent_color);
    }
    if (theme.accent_hover_color) {
      root.style.setProperty("--lc-accent-hover", theme.accent_hover_color);
    }
    if (theme.highlight_color) {
      root.style.setProperty("--lc-highlight", theme.highlight_color);
    }
    if (theme.font_display) {
      root.style.setProperty("--lc-font-display", `'${theme.font_display}', Fraunces, Georgia, serif`);
    }
    if (theme.font_body) {
      root.style.setProperty("--lc-font-body", `'${theme.font_body}', Inter, system-ui, sans-serif`);
    }

    return () => {
      root.style.removeProperty("--lc-accent");
      root.style.removeProperty("--lc-accent-hover");
      root.style.removeProperty("--lc-highlight");
      root.style.removeProperty("--lc-font-display");
      root.style.removeProperty("--lc-font-body");
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, trademark: PROTECTED_TRADEMARK_ZONES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return React.useContext(ThemeContext);
}
